import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Modal,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import LiveHelpIcon from "@mui/icons-material/LiveHelp";
import CloseIcon from "@mui/icons-material/Close";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import styled from "@emotion/styled";
import CircularProgress from "@mui/material/CircularProgress";
import { differenceInCalendarDays, format, isSameDay } from "date-fns";
import { enUS } from "date-fns/locale";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  downloadCSVFile,
  downloadExcelFile,
  downloadInvoiceFileUpdated,
  emailMultiPccTicketReport,
  getActiveReportEnabledCustomers,
  getCurrency,
  getCustomerByID,
  getCustomerStaticDataByCustID,
  getTpBatchReport,
} from "../redux/actions/AdminAction";
import {
  DOWNLOAD_INVOICE_RESET,
  EMAIL_MULTI_PCC_TICKET_REPORT_RESET,
  GET_CUSTOMER_RESET,
  GET_CUSTOMER_STATIC_DATA_RESET,
  GET_TP_BATCH_REPORT_RESET,
} from "../Constants/Constants";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#282828",
    color: "#ffffff",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#e8e9eb",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 400,
    fontSize: theme.typography.pxToRem(10),
    border: "1px solid #dadde9",
  },
}));

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 300,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 5,
  textAlign: "center",
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parsePccs = (value) =>
  String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.detail ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const isVoidTicket = (ticketStatus) =>
  String(ticketStatus || "").trim().toUpperCase() === "VOID" ||
  String(ticketStatus || "").trim().toUpperCase() === "REFUND";

const getTicketStatus = (ticket = {}) =>
  ticket?.ticket_status || ticket?.transaction || "";

const getTicketNumber = (ticket = {}) =>
  ticket?.number_display || ticket?.ticket_number_display || ticket?.number || "";

const getEticketCount = (ticket = {}) => Number(ticket?.eticket_count) || 0;

const formatFormOfPaymentLabel = (value) =>
  value === "Credit" ? "Credit Card" : value;

const formatTicketStatusLabel = (value, reportLanguage) => {
  const normalizedLanguage = String(reportLanguage || "").trim().toLowerCase();

  if (normalizedLanguage === "en" && value === "VENTE") {
    return "SALE";
  }

  return value;
};

function createData(ticket) {
  return {
    ticket_number: ticket?.number || "",
    ticket_status: ticket?.transaction || "",
    name: ticket?.traveler_name || "",
    date: ticket?.ticket_date || "",
    fare: ticket?.ht?.toString().trim() ? ticket.ht : 0,
    taxes: ticket?.total_taxe || 0,
    pnr: ticket?.pnr || "",
    currency: ticket?.device || "",
    fop: ticket?.formOfPayment || "",
  };
}

export default function MultiPccTicketReport(props) {
  const { decodedToken, checkUserPermission } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMediumScreen = useMediaQuery("(max-width:1800px)");
  const isSmallScreen = useMediaQuery("(max-width:1500px)");
  const token = JSON.parse(localStorage.getItem("userInfo"));
  const ticketStatusFilter = ["ALL", "VENTE", "VOID", "REFUND"];
  const formOfPaymentFilter = [
    "ALL",
    "CASH",
    "CHECK",
    "Credit",
    "MiscFormOfPayment",
  ];
  const today = new Date();
  const todayAtMidnight = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const minSelectableDate = new Date(todayAtMidnight.getFullYear(), 2, 1);
  const maxSelectableDate = new Date(todayAtMidnight);
  if (maxSelectableDate < minSelectableDate) {
    maxSelectableDate.setTime(minSelectableDate.getTime());
  }
  const minSelectableTimestamp = minSelectableDate.getTime();
  const createEmptyDateRange = () => ({
    startDate: null,
    endDate: new Date(0),
    key: "selection",
  });

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [pccOptions, setPccOptions] = useState([]);
  const [selectedPccs, setSelectedPccs] = useState([]);
  const [dateRange, setDateRange] = useState(createEmptyDateRange);
  const [datePickerResetKey, setDatePickerResetKey] = useState(0);
  const [currencies, setCurrencies] = useState({ default: "Default" });
  const [currency, setCurrency] = useState("default");
  const [reportType, setReportType] = useState("g");
  const [emailAddress, setEmailAddress] = useState("");
  const [reportGenerated, setReportGenerated] = useState(false);
  const [selectedTicketStatusFilter, setSelectTicketStatusFilter] =
    useState("ALL");
  const [selectedFormOfPaymentFilter, setSelectFormOfPaymentFilter] =
    useState("ALL");
  const [xlsxLoading, setXlsxLoading] = useState(false);
  const [xlsLoading, setXlsLoading] = useState(false);
  const [csvLoading, setCSVLoading] = useState(false);
  const [loadingRowId, setLoadingRowId] = useState(null);
  const [openInvoiceModel, setOpenInvoiceModel] = useState(false);
  const [openMultiTicketInvoiceDialog, setOpenMultiTicketInvoiceDialog] =
    useState(false);
  const [pendingInvoiceRequest, setPendingInvoiceRequest] = useState(null);
  const [addServiceFee, setAddServiceFee] = useState("no");
  const [serviceFeeAmount, setServiceFeeAmount] = useState("");
  const [serviceFeeOption, setServiceFeeOption] = useState("");
  const [snackOpen, setSnackOpen] = useState(false);
  const [alertType, setAlertType] = useState("error");
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const invoiceFileUrlRef = useRef(null);

  const { activeReportEnabledCustomersInfo } = useSelector(
    (state) => state.GetAllActiveReportEnabledCustomers
  );
  const { customerById } = useSelector((state) => state.GetCustomerByID);
  const { customerStaticDataInfo } = useSelector(
    (state) => state.GetCustomerStaticDataByCustID
  );
  const { tpBatchReportData, loading } = useSelector(
    (state) => state.GetTpBatchReportData
  );
  const { currencyInfo } = useSelector((state) => state.GetCurrency);
  const { downloadCSVLoading } = useSelector((state) => state.DownloadCSVFile);
  const { invoiceData, invoiceError } = useSelector(
    (state) => state.InvoiceDisplay
  );
  const { loading: emailLoading } = useSelector(
    (state) => state.EmailMultiPccTicketReport
  );

  const customers = useMemo(
    () => Object.values(activeReportEnabledCustomersInfo || {}),
    [activeReportEnabledCustomersInfo]
  );
  const rows = useMemo(() => {
    if (!Array.isArray(tpBatchReportData)) {
      return [];
    }

    let filteredTickets = [...tpBatchReportData];

    if (selectedTicketStatusFilter !== "ALL") {
      filteredTickets = filteredTickets.filter(
        (ticket) => ticket.transaction === selectedTicketStatusFilter
      );
    }

    if (selectedFormOfPaymentFilter !== "ALL") {
      filteredTickets = filteredTickets.filter(
        (ticket) => ticket.formOfPayment === selectedFormOfPaymentFilter
      );
    }

    const seenTickets = new Set();

    return filteredTickets.reduce((acc, ticket) => {
      const row = createData(ticket);
      const uniqueTicketKey = String(getTicketNumber(ticket) || row.ticket_number)
        .trim();

      if (!uniqueTicketKey || seenTickets.has(uniqueTicketKey)) {
        return acc;
      }

      seenTickets.add(uniqueTicketKey);
      acc.push(row);
      return acc;
    }, []);
  }, [
    selectedTicketStatusFilter,
    selectedFormOfPaymentFilter,
    tpBatchReportData,
  ]);
  const totals = useMemo(() => {
    if (!Array.isArray(rows)) {
      return { fares: "0.00", taxes: "0.00", amount: "0.00" };
    }

    const selectedStatus = String(selectedTicketStatusFilter || "")
      .trim()
      .toUpperCase();

    const rowsForTotals =
      selectedStatus === "ALL"
        ? rows.filter((row) => !isVoidTicket(row.ticket_status))
        : rows;

    const fares = rowsForTotals.reduce(
      (acc, item) => acc + (Number(item?.fare) || 0),
      0
    );
    const taxes = rowsForTotals.reduce(
      (acc, item) => acc + (Number(item?.taxes) || 0),
      0
    );

    return {
      fares: fares.toFixed(2),
      taxes: taxes.toFixed(2),
      amount: (fares + taxes).toFixed(2),
    };
  }, [rows, selectedTicketStatusFilter]);
  const reportLanguage =
    customerById?.report_language?.trim() ||
    customerById?.[0]?.report_language?.trim() ||
    "";
  const selectedStartDate = dateRange.startDate
    ? new Date(
        dateRange.startDate.getFullYear(),
        dateRange.startDate.getMonth(),
        dateRange.startDate.getDate()
      )
    : null;
  const isTodaySelected = selectedStartDate
    ? isSameDay(selectedStartDate, todayAtMidnight)
    : false;
  const isLiveDataDateSelected = isTodaySelected;
  const isPastDateSelected = selectedStartDate
    ? selectedStartDate.getTime() < todayAtMidnight.getTime()
    : false;
  const normalizeDate = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const isDateDisabled = (day) => {
    const normalizedDay = normalizeDate(day);

    if (isTodaySelected) {
      return !isSameDay(normalizedDay, todayAtMidnight);
    }

    if (isPastDateSelected) {
      return isSameDay(normalizedDay, todayAtMidnight);
    }

    return false;
  };

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }

    if (!checkUserPermission(103, "can_view")) {
      navigate("/home", { replace: true });
      return;
    }

    dispatch(getActiveReportEnabledCustomers());
    dispatch(getCurrency());

    return () => {
      dispatch({ type: GET_CUSTOMER_RESET });
      dispatch({ type: GET_CUSTOMER_STATIC_DATA_RESET });
      dispatch({ type: GET_TP_BATCH_REPORT_RESET });
      dispatch({ type: EMAIL_MULTI_PCC_TICKET_REPORT_RESET });
    };
  }, [checkUserPermission, dispatch, navigate, token]);

  useEffect(() => {
    if (customerId) {
      setReportType("g");
      dispatch(getCustomerStaticDataByCustID(customerId));
    }
  }, [customerId, dispatch]);

  useEffect(() => {
    if (customerById) {
      const pccs = parsePccs(customerById?.pccs);
      setPccOptions(pccs);
      setSelectedPccs(pccs.length === 1 ? pccs : []);
      setCustomerName((currentCustomerName) =>
        customerById?.customer_name?.trim() ||
          customerById?.[0]?.customer_name?.trim() ||
          currentCustomerName
      );
    }
  }, [customerById]);

  useEffect(() => {
    if (invoiceData) {
      setLoadingRowId(null);
      setOpenInvoiceModel(true);
    }
  }, [invoiceData]);

  useEffect(() => {
    if (invoiceError) {
      setLoadingRowId(null);
    }
  }, [invoiceError]);

  useEffect(() => {
    invoiceFileUrlRef.current = invoiceData?.fileUrl || null;
  }, [invoiceData]);

  useEffect(() => {
    if (currencyInfo) {
      setCurrencies((prevState) => ({ ...prevState, ...currencyInfo }));
      setCurrency("default");
    }
  }, [currencyInfo]);

  useEffect(() => {
    dispatch({ type: GET_CUSTOMER_RESET });
    dispatch({ type: GET_CUSTOMER_STATIC_DATA_RESET });
    dispatch({ type: GET_TP_BATCH_REPORT_RESET });
    dispatch({ type: DOWNLOAD_INVOICE_RESET });

    return () => {
      if (invoiceFileUrlRef.current) {
        URL.revokeObjectURL(invoiceFileUrlRef.current);
      }
      dispatch({ type: DOWNLOAD_INVOICE_RESET });
      dispatch({ type: GET_TP_BATCH_REPORT_RESET });
    };
  }, [dispatch]);

  useEffect(() => {
    if (
      reportGenerated &&
      Array.isArray(tpBatchReportData) &&
      tpBatchReportData.length === 0
    ) {
      showAlert(
        "success",
        "No Data!",
        "No report data is available for the selected PCC and date range."
      );
      setReportGenerated(false);
    }
  }, [reportGenerated, tpBatchReportData]);

  const showAlert = (type, title, message) => {
    setAlertType(type);
    setAlertTitle(title);
    setAlertMessage(message);
    setSnackOpen(true);
  };

  const handleCloseSnack = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackOpen(false);
  };

  const clearReportData = () => {
    setReportGenerated(false);
    setSelectTicketStatusFilter("ALL");
    setSelectFormOfPaymentFilter("ALL");
    setLoadingRowId(null);
    setOpenInvoiceModel(false);
    setOpenMultiTicketInvoiceDialog(false);
    setPendingInvoiceRequest(null);
    setAddServiceFee("no");
    setServiceFeeAmount("");
    setServiceFeeOption("");
    if (invoiceFileUrlRef.current) {
      URL.revokeObjectURL(invoiceFileUrlRef.current);
      invoiceFileUrlRef.current = null;
    }
    dispatch({ type: DOWNLOAD_INVOICE_RESET });
    dispatch({ type: GET_TP_BATCH_REPORT_RESET });
  };

  const handleResetDateRange = () => {
    clearReportData();
    setDateRange(createEmptyDateRange());
    setDatePickerResetKey((currentKey) => currentKey + 1);
    setSelectedPccs([]);
    setEmailAddress("");
  };

  const isSelectableDate = (date) =>
    date instanceof Date &&
    !Number.isNaN(date.getTime()) &&
    date.getTime() >= minSelectableTimestamp;

  const formatSelectedDate = (date) =>
    isSelectableDate(date) ? format(date, "yyyy-MM-dd") : null;

  const formatSelectedDateLabel = (date) =>
    isSelectableDate(date) ? format(date, "ddMMMyy").toUpperCase() : "";

  const getFormattedDates = () => ({
    from_date: formatSelectedDate(dateRange.startDate),
    to_date: formatSelectedDate(dateRange.endDate),
  });

  const validateBaseFilters = () => {
    if (!customerId) {
      showAlert("error", "Error!", "Please choose the customer name.");
      return false;
    }

    if (selectedPccs.length === 0) {
      showAlert("error", "Error!", "Please select at least one PCC.");
      return false;
    }

    if (
      !isSelectableDate(dateRange.startDate) ||
      !isSelectableDate(dateRange.endDate)
    ) {
      showAlert("error", "Error!", "Please select the date range.");
      return false;
    }

    if (differenceInCalendarDays(dateRange.endDate, dateRange.startDate) > 30) {
      showAlert(
        "error",
        "Error!",
        "Ticket report is allowed to generate only for 31 days."
      );
      return false;
    }

    return true;
  };

  const handleGenerateReport = () => {
    if (isLiveDataDateSelected) {
      showAlert(
        "info",
        "Live Data!",
        "Today's report date can only be sent by email. Please use Send Report Email."
      );
      return;
    }

    clearReportData();

    if (!validateBaseFilters()) {
      return;
    }

    if (selectedPccs.length !== 1) {
      showAlert(
        "info",
        "Email Required!",
        "Preview is available only when a single PCC is selected. Please use Send Report Email for multiple PCCs."
      );
      return;
    }

    const { from_date, to_date } = getFormattedDates();
    dispatch(
      getTpBatchReport(customerId, selectedPccs[0], reportType, from_date, to_date)
    ).then(() => {
      setReportGenerated(true);
    });
  };

  const handleSendEmail = () => {
    if (!validateBaseFilters()) {
      return;
    }

    const emails = emailAddress
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email !== "");

    if (
      emails.length === 0 ||
      !emails.every((email) => emailPattern.test(email))
    ) {
      showAlert(
        "error",
        "Error!",
        "Please enter valid email address(es), separated by commas."
      );
      return;
    }

    const { from_date, to_date } = getFormattedDates();
    const requestData = {
      customer_id: customerId,
      customer_name: customerName,
      pccs: selectedPccs,
      email: emailAddress.trim(),
      report_type: reportType,
      from_date,
      to_date,
      currency: currency === "default" ? null : currency,
      report_language: reportLanguage,
      requested_by:
        decodedToken?.email || decodedToken?.username || decodedToken?.sub || "",
    };

    dispatch(emailMultiPccTicketReport(requestData))
      .then((data) => {
        showAlert(
          "success",
          "Email Sent!",
          data?.message || "Ticket report email request has been submitted."
        );
      })
      .catch((error) => {
        showAlert(
          "error",
          "Error!",
          getErrorMessage(error, "Unable to send ticket report email.")
        );
      });
  };

  const selectedCurrencyOption =
    currency === "default"
      ? ["default", "Default"]
      : [currency, currencies[currency]];
  const isMultiplePccSelected = selectedPccs.length > 1;
  const selectedPcc = selectedPccs[0];
  const downloadRequestData = useMemo(
    () => ({
      customer_id: customerId,
      customer_name: customerName,
      pcc: selectedPcc,
      pccs: selectedPccs,
      report_type: reportType,
      from_date:
        dateRange.startDate instanceof Date &&
        !Number.isNaN(dateRange.startDate.getTime()) &&
        dateRange.startDate.getTime() >= minSelectableTimestamp
        ? format(dateRange.startDate, "yyyy-MM-dd")
        : null,
      to_date:
        dateRange.endDate instanceof Date &&
        !Number.isNaN(dateRange.endDate.getTime()) &&
        dateRange.endDate.getTime() >= minSelectableTimestamp
        ? format(dateRange.endDate, "yyyy-MM-dd")
        : null,
      currency: currency === "default" ? rows[0]?.currency || null : currency,
      report_language: reportLanguage,
      ticketReportInfo: tpBatchReportData,
      total_fares: totals.fares,
      total_taxes: totals.taxes,
      total_amount: totals.amount,
    }),
    [
      customerId,
      customerName,
      dateRange.endDate,
      dateRange.startDate,
      currency,
      reportLanguage,
      reportType,
      minSelectableTimestamp,
      rows,
      selectedPcc,
      selectedPccs,
      totals.amount,
      totals.fares,
      totals.taxes,
      tpBatchReportData,
    ]
  );

  // const handleCSVDownload = () => {
  //   if (!rows.length) {
  //     showAlert("error", "Error!", "No data to download!");
  //     return;
  //   }

  //   dispatch(
  //     downloadCSVFile(customerName, { file_type: "csv" }, reportType)
  //   );
  // };

  const handleExcelDownload = (fileFormat) => {
    if (!rows.length) {
      showAlert("error", "Error!", "No data to download!");
      return;
    }

    if (fileFormat === "xlsx") {
      setXlsxLoading(true);
    } else if (fileFormat === "xls") {
      setXlsLoading(true);
    }else if (fileFormat === "csv") {
      setCSVLoading(true);
    }

    dispatch(
      downloadExcelFile(customerName, downloadRequestData, reportType, fileFormat)
    )
      .then(() => {
        if (fileFormat === "xlsx") {
          setXlsxLoading(false);
        } else if (fileFormat === "xls") {
          setXlsLoading(false);
        }
         else if (fileFormat === "csv") {
          setCSVLoading(false);
        }
      })
      .catch(() => {
        if (fileFormat === "xlsx") {
          setXlsxLoading(false);
        } else if (fileFormat === "xls") {
          setXlsLoading(false);
        } else if (fileFormat === "csv") {
          setCSVLoading(false);
        }
      });
  };

  const resetInvoicePreview = () => {
    if (invoiceData?.fileUrl) {
      URL.revokeObjectURL(invoiceData.fileUrl);
    }
    dispatch({ type: DOWNLOAD_INVOICE_RESET });
  };

  const handleCloseInvoiceModel = () => {
    setOpenInvoiceModel(false);
    setLoadingRowId(null);
    resetInvoicePreview();
  };

  const handleCloseMultiTicketInvoiceDialog = () => {
    setOpenMultiTicketInvoiceDialog(false);
    setPendingInvoiceRequest(null);
    setAddServiceFee("no");
    setServiceFeeAmount("");
    setServiceFeeOption("");
  };

  const getServiceFeeRequestData = () => {
    const shouldAddServiceFee = addServiceFee === "yes";

    return {
      add_service_fee: shouldAddServiceFee,
      service_fee_amount: shouldAddServiceFee ? Number(serviceFeeAmount) : "",
      service_fee_option: shouldAddServiceFee ? serviceFeeOption : "",
    };
  };

  const validateServiceFeeSelection = () => {
    if (addServiceFee !== "yes") {
      return true;
    }

    const normalizedAmount = String(serviceFeeAmount || "").trim();
    const parsedAmount = Number(normalizedAmount);

    if (!normalizedAmount || Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      showAlert("error", "Error!", "Please enter a valid service fee amount.");
      return false;
    }

    if (!serviceFeeOption) {
      showAlert(
        "error",
        "Error!",
        "Please select how service fees should appear in the invoice."
      );
      return false;
    }

    return true;
  };

  const runInvoiceDownloadFlow = (
    selectedTicket,
    fileType = "pdf",
    downloadMultipleInvoice = false,
    serviceFeeDetails = {}
  ) => {
    if (!selectedTicket) {
      return;
    }

    const ticketNumber = selectedTicket.number || selectedTicket.ticket_number;
    const requestData = {
      pcc_code: selectedPccs[0] || "",
      data: selectedTicket,
      customer_id: customerId,
      usertype: decodedToken?.role,
      download_multiple_invoice: downloadMultipleInvoice,
      ...serviceFeeDetails,
    };

    setLoadingRowId(ticketNumber);
    dispatch(downloadInvoiceFileUpdated(requestData, fileType));
  };

  const handleInvoiceDownload = (row) => {
    if (!row || !row.ticket_number) {
      showAlert("error", "Error!", "Invalid row data!");
      return;
    }

    if (isVoidTicket(row.ticket_status)) {
      showAlert("error", "Error!", "Invoice is not available for VOID tickets.");
      return;
    }

    const reportTickets = Array.isArray(tpBatchReportData) ? tpBatchReportData : [];

    if (reportTickets.length === 0) {
      showAlert("error", "Error!", "Ticket details not available!");
      return;
    }

    const selectedTicket = reportTickets.find(
      (item) =>
        String(item.number || "").trim() === String(row.ticket_number).trim()
    );

    if (!selectedTicket) {
      showAlert("error", "Error!", "Ticket details not found!");
      return;
    }

    if (isVoidTicket(getTicketStatus(selectedTicket))) {
      showAlert("error", "Error!", "Invoice is not available for VOID tickets.");
      return;
    }

    const eticketCount = getEticketCount(selectedTicket);

    setPendingInvoiceRequest({
      selectedTicket,
      eticketCount,
      pnr: selectedTicket.pnr,
    });
    setAddServiceFee("no");
    setServiceFeeAmount("");
    setServiceFeeOption("");
    setOpenMultiTicketInvoiceDialog(true);
  };

  const handleSingleInvoiceDownload = () => {
    if (!pendingInvoiceRequest?.selectedTicket) {
      handleCloseMultiTicketInvoiceDialog();
      return;
    }

    if (!validateServiceFeeSelection()) {
      return;
    }

    const serviceFeeDetails = getServiceFeeRequestData();
    const { selectedTicket } = pendingInvoiceRequest;
    handleCloseMultiTicketInvoiceDialog();
    runInvoiceDownloadFlow(selectedTicket, "pdf", false, serviceFeeDetails);
  };

  const handleAllPaxInvoiceDownload = () => {
    if (!pendingInvoiceRequest?.selectedTicket) {
      handleCloseMultiTicketInvoiceDialog();
      return;
    }

    if (!validateServiceFeeSelection()) {
      return;
    }

    const serviceFeeDetails = getServiceFeeRequestData();
    const { selectedTicket } = pendingInvoiceRequest;
    handleCloseMultiTicketInvoiceDialog();
    runInvoiceDownloadFlow(selectedTicket, "pdf", true, serviceFeeDetails);
  };

  return (
    <Box sx={{ marginBottom: "5%" }}>
      <Container
        sx={{
          width: "100%",
          marginBottom: "5%",
          justifyContent: "center",
        }}
      >
        <Box sx={{ margin: 3, fontSize: 25, textAlign: "center" }}>
          Multi PCC Ticket Report
          <Divider sx={{ marginTop: 2 }}></Divider>
        </Box>

        <Grid
          container
          mt={1}
          sx={{
            padding: 2,
            borderRadius: 2,
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <Grid xs={4} mt={2}>
            <Typography>Customer Name</Typography>
          </Grid>
          <Grid xs={4} mt={2}>
            <Typography>PCC</Typography>
          </Grid>
          <Grid xs={4} mt={2}>
            <Typography>Currency</Typography>
          </Grid>
          <Grid
            xs={4}
            mt={1}
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Autocomplete
              size="small"
              sx={{ width: "90%" }}
              value={selectedCustomer}
              options={customers}
              getOptionLabel={(option) => option?.customer_name?.trim() || ""}
              isOptionEqualToValue={(option, value) =>
                option?.customer_id === value?.customer_id
              }
              renderInput={(params) => <TextField {...params} variant="outlined" />}
              onChange={(e, values) => {
                clearReportData();
                setSelectedCustomer(values);
                setEmailAddress("");
                if (values) {
                  dispatch(getCustomerByID(values.customer_id));
                  setCustomerId(values.customer_id);
                  setCustomerName(values.customer_name?.trim());
                } else {
                  dispatch({ type: GET_CUSTOMER_RESET });
                  dispatch({ type: GET_CUSTOMER_STATIC_DATA_RESET });
                  setCustomerId(null);
                  setCustomerName("");
                  setPccOptions([]);
                  setSelectedPccs([]);
                }
              }}
            />
          </Grid>
          <Grid
            xs={4}
            mt={1}
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Autocomplete
              multiple
              size="small"
              sx={{ width: "90%" }}
              value={selectedPccs}
              options={pccOptions}
              getOptionLabel={(option) => option}
              renderInput={(params) => <TextField {...params} variant="outlined" />}
              onChange={(e, values) => {
                clearReportData();
                setSelectedPccs(values);
              }}
            />
          </Grid>
          <Grid
            xs={4}
            mt={1}
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Autocomplete
              size="small"
              sx={{ width: "90%" }}
              value={selectedCurrencyOption}
              options={Object.entries(currencies)}
              isOptionEqualToValue={(option, value) => option[0] === value[0]}
              getOptionLabel={(option) =>
                option[0] === "default"
                  ? "Default"
                  : `${option[0]} - ${option[1]}`
              }
              renderInput={(params) => <TextField {...params} variant="outlined" />}
              onChange={(e, values) => {
                setCurrency(values ? values[0] : "default");
              }}
            />
          </Grid>

          {customerStaticDataInfo === undefined ? (
            <Grid item xs={12} mt={3}>
              <FormControl>
                <RadioGroup
                  row
                  defaultValue={reportType}
                  value={reportType}
                  onChange={(event) => {
                    clearReportData();
                    setReportType(event.target.value);
                  }}
                >
                  <FormControlLabel
                    value="g"
                    control={<Radio />}
                    label="General Ticket Report"
                  />
                  <FormControlLabel
                    value="c"
                    control={<Radio />}
                    label="Advanced Ticket Report"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          ) : null}

          <Grid
            xs={12}
            mt={3}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography>Please choose your Date Range</Typography>
            <Button size="small" variant="text" onClick={handleResetDateRange}>
              Reset
            </Button>
          </Grid>
          <Grid xs={12} mt={2}>
            <DateRangePicker
              key={datePickerResetKey}
              ranges={[dateRange]}
              locale={enUS}
              maxDate={maxSelectableDate}
              minDate={minSelectableDate}
              disabledDay={isDateDisabled}
              onChange={(item) => {
                const nextSelection = item.selection || {};
                const nextStartDate = nextSelection.startDate
                  ? normalizeDate(nextSelection.startDate)
                  : null;
                const nextEndDate = nextSelection.endDate
                  ? normalizeDate(nextSelection.endDate)
                  : null;
                const nextDates = [nextStartDate, nextEndDate].filter(Boolean);
                const hasToday = nextDates.some((date) =>
                  isSameDay(date, todayAtMidnight)
                );
                const hasPastDate = nextDates.some(
                  (date) => date.getTime() < todayAtMidnight.getTime()
                );

                if (hasToday && hasPastDate) {
                  showAlert(
                    "error",
                    "Error!",
                    "Please select either today's date only or a past date range."
                  );
                  return;
                }

                clearReportData();
                setDateRange({
                  key: nextSelection.key || "selection",
                  ...item.selection,
                  startDate: nextStartDate || item.selection.startDate,
                  endDate: nextEndDate || item.selection.endDate,
                });
              }}
              editableDateInputs={true}
              rangeColors={["black"]}
            />
          </Grid>
          <Grid xs={12} mt={2} sx={{ color: "red" }}>
            Please note that ticket report is allowed to generate only for 31
            days.
          </Grid>
          {isMultiplePccSelected ? (
            <Grid xs={12} mt={1} sx={{ color: "#ed6c02" }}>
              Note: Preview is available only when a single PCC is selected. Please
              use Send Report Email for multiple PCCs.
            </Grid>
          ) : null}
          {isLiveDataDateSelected ? (
            <Grid xs={12} mt={1} sx={{ color: "#ed6c02" }}>
              Note: Today's report date is live data. Please use Send Report
              Email.
            </Grid>
          ) : null}
          <Grid
            xs={12}
            mt={3}
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <TextField
              size="small"
              sx={{ width: "50%" }}
              label="Email Address(es)"
              helperText="Separate multiple email addresses with commas."
              value={emailAddress}
              onChange={(event) => setEmailAddress(event.target.value)}
            />
          </Grid>
          <Grid
            xs={12}
            mt={3}
            mb={1}
            sx={{ display: "flex", gap: 2, justifyContent: "center" }}
          >
            <LoadingButton
              loading={loading}
              loadingPosition="start"
              onClick={handleGenerateReport}
              variant="contained"
              type="submit"
              disabled={isMultiplePccSelected || isLiveDataDateSelected}
              sx={{ backgroundColor: "#282828", width: "20%" }}
            >
              Generate Report
            </LoadingButton>
            <LoadingButton
              loading={emailLoading}
              loadingPosition="start"
              onClick={handleSendEmail}
              variant="contained"
              sx={{ backgroundColor: "#282828", width: "20%" }}
            >
              Send Report Email
            </LoadingButton>
          </Grid>
        </Grid>
      </Container>
 {/* Ticket Report Data List */}
      {loading ? (
        <Modal open={loading}>
          <Box sx={modalStyle}>
            <Grid container>
              <Grid xs={12}>
                <CircularProgress />
              </Grid>
              <Grid xs={12} mt={3}>
                <Typography>
                  Just a moment, we're preparing everything for you.
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Modal>
      ) : rows ? (
        <Box sx={{  display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center", }}>
          <Box sx={{ mb: 2, textAlign: "center" }}>
            <Typography sx={{ fontSize: 20 }}>
              {customerName} | {selectedPcc} |{" "}
              {formatSelectedDateLabel(dateRange.startDate)}
              {dateRange.startDate && dateRange.endDate ? "-" : ""}
              {formatSelectedDateLabel(dateRange.endDate)}
            </Typography>
          </Box>
          <Box
            sx={{
           display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              // textAlign: "center",
              // width: "70%",
              width: isSmallScreen ? "95%" : isMediumScreen ? "70%" : "70%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                width: "50%",
                justifyContent: "left",
              }}
            >
              <Autocomplete
                size="small"
                sx={{
                  width: isSmallScreen ? "40%" : isMediumScreen ? "30%" : "25%",
                }}
                options={ticketStatusFilter || []}
                value={selectedTicketStatusFilter || null}
                getOptionLabel={(options) =>
                  formatTicketStatusLabel(options, reportLanguage)
                }
                renderInput={(params) => {
                  return (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Ticket Status"
                    />
                  );
                }}
                onChange={(e, values) => {
                  if (values) {
                    setSelectTicketStatusFilter(values);
                  }
                }}
              />
              <Autocomplete
                size="small"
                sx={{
                  width: isSmallScreen ? "50%" : isMediumScreen ? "40%" : "35%",
                  marginLeft: 3,
                }}
                value={selectedFormOfPaymentFilter || null}
                options={formOfPaymentFilter || []}
                getOptionLabel={(options) => formatFormOfPaymentLabel(options)}
                renderInput={(params) => {
                  return (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Form Of Payment"
                    />
                  );
                }}
                onChange={(e, values) => {
                  if (values) {
                    if (values !== "ALL") {
                      let formattedValue;
                      if (values === "MiscFormOfPayment") {
                        formattedValue = values;
                      } else if (values === "Credit") {
                        formattedValue = values;
                      } else {
                        formattedValue =
                          values.charAt(0).toUpperCase() +
                          values.slice(1).toLowerCase();
                      }
                      setSelectFormOfPaymentFilter(formattedValue);
                    } else {
                      setSelectFormOfPaymentFilter(values);
                    }
                  }
                }}
              />
            </Box>
          <Box
            sx={{
               display: "flex",
                width: "50%",
                justifyContent: "right",
            }}
          >
            <LoadingButton
              loading={xlsxLoading}
              loadingIndicator="Downloading..."
              onClick={() => handleExcelDownload("xlsx")}
              variant="contained"
              sx={{     backgroundColor: "#282828",
                  width: isSmallScreen ? "40%" : isMediumScreen ? "30%" : "25%",}}
            >
              Download .xlsx
            </LoadingButton>
            <LoadingButton
              loading={xlsLoading}
              loadingIndicator="Downloading..."
              onClick={() => handleExcelDownload("xls")}
              variant="contained"
              sx={{   backgroundColor: "#282828",
                  width: isSmallScreen ? "40%" : isMediumScreen ? "30%" : "25%",
                  marginLeft: 3, }}
            >
              Download .xls
            </LoadingButton>
            <LoadingButton
              loading={csvLoading}
              loadingIndicator="Downloading..."
              onClick={() => handleExcelDownload("csv")}
              variant="contained"
              sx={{   backgroundColor: "#282828",
                  width: isSmallScreen ? "40%" : isMediumScreen ? "30%" : "25%",
                  marginLeft: 3, }}
            >
              Download CSV
            </LoadingButton>
            <HtmlTooltip
              title={
                <Fragment>
                  <Typography sx={{ fontWeight: "bold" }}>
                    This setting only takes effect after downloading the Excel
                    file.
                  </Typography>
                  <br />
                  <Typography sx={{ fontWeight: "bold" }}>
                    Changing regional settings in Web Excel
                  </Typography>
                  <Typography>- Select File</Typography>
                  <Typography>- Select Options</Typography>
                  <Typography>- Select Regional Settings</Typography>
                  <Typography>
                    - Select a region from the drop-down list
                  </Typography>
                  <Typography>- Select Change</Typography>
                  <br />
                  <Typography sx={{ fontWeight: "bold" }}>
                    Changing regional settings in Windows
                  </Typography>
                  <Typography>- Select the Start button</Typography>
                  <Typography>- Select Settings</Typography>
                  <Typography>- Select Time & language</Typography>
                  <Typography>- Select Region</Typography>
                  <Typography>
                    - Select Change date, time, or number formats
                  </Typography>
                  <br />
                  <Typography sx={{ fontWeight: "bold" }}>
                    If your date format is already set to DD/MM/YYYY, you can
                    ignore these instructions.
                  </Typography>
                </Fragment>
              }
              arrow
            >
              <LiveHelpIcon fontSize="large" sx={{ color: "red" }} />
            </HtmlTooltip>
        
           </Box>
          </Box>
          <Box
            sx={{
          marginTop: 3,
              width: isSmallScreen ? "95%" : isMediumScreen ? "70%" : "70%",
            }}
          >
            <Box sx={{ mb: 2 }}>
              <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Currency</StyledTableCell>
                      <StyledTableCell>Total Fares</StyledTableCell>
                      <StyledTableCell>Total Taxes</StyledTableCell>
                      <StyledTableCell>Total Amount</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <StyledTableCell component="th" scope="row">
                        {currency !== "default" ? currency : rows[0]?.currency}
                      </StyledTableCell>
                      <StyledTableCell>{totals.fares}</StyledTableCell>
                      <StyledTableCell>{totals.taxes}</StyledTableCell>
                      <StyledTableCell>{totals.amount}</StyledTableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Date</StyledTableCell>
                    <StyledTableCell>PNR</StyledTableCell>
                    <StyledTableCell>Name</StyledTableCell>
                    <StyledTableCell>Ticket Number</StyledTableCell>
                    <StyledTableCell>Fare</StyledTableCell>
                    <StyledTableCell>Taxes</StyledTableCell>
                    <StyledTableCell>Total</StyledTableCell>
                    <StyledTableCell>Ticket Status</StyledTableCell>
                    <StyledTableCell>FOP</StyledTableCell>
                    <StyledTableCell>Currency</StyledTableCell>
                    <StyledTableCell>Invoice</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows?.length > 0 ? (
                  rows.map((row, index) => (
                    <StyledTableRow
                      key={`${row.ticket_number}-${row.pnr}-${index}`}
                    >
                      <StyledTableCell component="th" scope="row">
                        {row.date}
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        {row.pnr}
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        {row.name.trim()}
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        {row.ticket_number}
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        {row.fare || 0}
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        {row.taxes}
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        {(
                          (Number(row.fare) || 0) + (Number(row.taxes) || 0)
                        ).toFixed(2)}
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        {formatTicketStatusLabel(
                          row.ticket_status,
                          reportLanguage
                        )}
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        {formatFormOfPaymentLabel(row.fop)}
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        {row.currency}
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        <LoadingButton
                          loading={loadingRowId === row.ticket_number}
                          loadingIndicator="Preparing..."
                          disabled={isVoidTicket(row.ticket_status)}
                          onClick={() => handleInvoiceDownload(row)}
                          variant="contained"
                          sx={{
                            backgroundColor: "#282828",
                            width: "80%",
                            marginLeft: 3,
                          }}
                        >
                          View Invoice
                        </LoadingButton>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                  ) : (
                    <StyledTableRow
                      style={{
                        wordWrap: "break-word",
                        maxWidth: "100%",
                      }}
                    >
                      <StyledTableCell
                        component="th"
                        colSpan={11}
                        scope="row"
                        style={{
                          textAlign: "center",
                          wordWrap: "break-word",
                          maxWidth: "100%",
                          fontSize: 15,
                        }}
                      >
                        No data found
                      </StyledTableCell>
                    </StyledTableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      ) : null}

     <Snackbar
  open={snackOpen}
  onClose={handleCloseSnack}
  autoHideDuration={5000} // 10 seconds
  anchorOrigin={{
    vertical: "top",
    horizontal: "center",
  }}
>
  <Alert
    severity={alertType}
    onClose={handleCloseSnack}

    sx={{ width: "100%" }}
  >
    <AlertTitle>{alertTitle}</AlertTitle>
    {alertMessage}
  </Alert>
</Snackbar>
      <Dialog
        open={openInvoiceModel}
        onClose={handleCloseInvoiceModel}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Invoice Preview
          <IconButton
            onClick={handleCloseInvoiceModel}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ height: "75vh" }}>
          {invoiceData && !invoiceError && (
            <object
              data={invoiceData.fileUrl}
              type="application/pdf"
              width="100%"
              height="100%"
              aria-label="Invoice preview"
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              const link = document.createElement("a");
              link.href = invoiceData.fileUrl;
              link.download = invoiceData.fileName;
              link.click();
            }}
          >
            Download Invoice
          </Button>

          <Button variant="outlined" onClick={handleCloseInvoiceModel}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openMultiTicketInvoiceDialog}
        onClose={handleCloseMultiTicketInvoiceDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Invoice Options</DialogTitle>
        <DialogContent dividers>
          {pendingInvoiceRequest?.eticketCount > 1 ? (
            <Typography>
              PNR {pendingInvoiceRequest?.pnr || "-"} has multiple e-tickets
              {pendingInvoiceRequest?.eticketCount
                ? ` (${pendingInvoiceRequest.eticketCount} found)`
                : ""}
              . Do you want to download a single invoice or an invoice for all
              pax?
            </Typography>
          ) : (
            <Typography>
              Configure the invoice options below and continue to preview the
              invoice.
            </Typography>
          )}

          <FormControl component="fieldset" sx={{ mt: 3, width: "100%" }}>
            <FormLabel>Do you want to add service fees?</FormLabel>
            <RadioGroup
              row
              value={addServiceFee}
              onChange={(event) => {
                const value = event.target.value;
                setAddServiceFee(value);
                if (value !== "yes") {
                  setServiceFeeAmount("");
                  setServiceFeeOption("");
                }
              }}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>

          {addServiceFee === "yes" && (
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Service Fee Amount"
                type="number"
                size="small"
                fullWidth
                value={serviceFeeAmount}
                onChange={(event) => setServiceFeeAmount(event.target.value)}
                inputProps={{ min: 0, step: "0.01" }}
              />

              <FormControl component="fieldset" sx={{ mt: 2, width: "100%" }}>
                <FormLabel>How should service fees be handled?</FormLabel>
                <RadioGroup
                  value={serviceFeeOption}
                  onChange={(event) => setServiceFeeOption(event.target.value)}
                >
                  <FormControlLabel
                    value="included_in_final_amount_invisible"
                    control={<Radio />}
                    label="Include service fees in the final invoice amount, and do not keep them separate (invisible)"
                  />
                  <FormControlLabel
                    value="included_in_final_amount_visible"
                    control={<Radio />}
                    label="Include service fees in the final invoice amount, and keep them separate (visible)"
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseMultiTicketInvoiceDialog}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSingleInvoiceDownload}
            variant={
              pendingInvoiceRequest?.eticketCount > 1 ? "outlined" : "contained"
            }
          >
            {pendingInvoiceRequest?.eticketCount > 1
              ? "Single Invoice"
              : "View Invoice"}
          </Button>
          {pendingInvoiceRequest?.eticketCount > 1 && (
            <Button onClick={handleAllPaxInvoiceDownload} variant="contained">
              Invoice For All Pax
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
